import React, { FC, useState, useCallback, useMemo } from 'react';
import { useTranslate } from '@/translations';

import { AuditActionType, AuditLog } from '@/types';

import { Modal } from 'antd';
import { StyledLink } from '@/ui/globalStyles';
import {
  faArrowsRotate,
  Icon,
  CustomColumns,
  CustomTable,
} from '@/ui/components';
import { currencyFormatted, localDateTimeFormatted } from '@/utils/helpers';

import { EventViewerDataGridProps } from './types';
import { EntityDetails } from './components/EntityDetails';

const MECHANICAL_INSPECTION_RESENT_TITLE_PREFIX =
  'Mechanical inspection resent for VIN';

const isMechanicalInspectionResentEvent = (data: AuditLog) =>
  data.eventTitle?.startsWith(MECHANICAL_INSPECTION_RESENT_TITLE_PREFIX) ??
  false;

const formatEventData = (eventData?: string) => {
  if (!eventData) return '';
  try {
    return JSON.stringify(JSON.parse(eventData), null, 2);
  } catch {
    return eventData;
  }
};

export const EventViewerDataGrid: FC<EventViewerDataGridProps> = ({
  loading,
  dataSource,
  reload,
  inspectionId,
}) => {
  const [showDetails, setShowDetails] = useState<AuditLog>();
  const { t } = useTranslate();

  const renderEntityDetails = useCallback(
    (data: AuditLog) => (
      <EntityDetails data={data} inspectionId={inspectionId} />
    ),
    [inspectionId],
  );

  const renderDataChanges = useCallback((data: AuditLog) => {
    if (isMechanicalInspectionResentEvent(data)) {
      return (
        <pre
          data-testid="mechanical-inspection-resent-event-data"
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            margin: 0,
            maxHeight: 480,
            overflow: 'auto',
          }}
        >
          {formatEventData(data.eventData)}
        </pre>
      );
    }

    if (!data || !data.eventObject) return null;

    const changes = data.eventObject;
    const currencyFields = ['FlatRatePrice'];

    const getShowValue = (val: any, key: string) => {
      if (val === null || val === undefined) return null;
      if (typeof val === 'boolean') return val ? 'Yes' : 'No';
      if (typeof val === 'number')
        return currencyFields.includes(key) ? currencyFormatted(val) : val;
      if (Array.isArray(val)) return null;
      if (typeof val === 'object') return null;
      const checkDate = Date.parse(val);
      if (
        !Number.isNaN(checkDate) &&
        checkDate > 0 &&
        val.split(new RegExp(['\\.', '\\-', '\\/'].join('|'), 'g')).length > 2
      )
        return localDateTimeFormatted(val);
      return val;
    };

    const keys = Object.keys(changes)
      .filter((key) => changes[key]?.Old || changes[key]?.New)
      .sort()
      .map((key) => ({
        key,
        label: key,
        from: changes[key].Old,
        to: changes[key].New,
      }));

    return keys.length > 0
      ? keys
          .filter(({ key }) => changes[key])
          .map(({ key, label, from, to }) => (
            <div key={key}>
              <strong>{label}</strong>:{' '}
              {data.actionType === AuditActionType.Update ? (
                <>
                  changed from <b>{getShowValue(from, key)}</b> to{' '}
                  <b>{getShowValue(to, key)}</b>
                </>
              ) : (
                <span
                  style={{
                    textDecoration:
                      data.actionType === AuditActionType.Delete
                        ? 'line-through'
                        : undefined,
                    color:
                      data.actionType === AuditActionType.UnDelete
                        ? 'green'
                        : undefined,
                  }}
                >
                  {getShowValue(
                    data.actionType === AuditActionType.Delete ? from : to,
                    key,
                  )}
                </span>
              )}
            </div>
          ))
      : 'Cannot show changes for old data';
  }, []);

  const columns: CustomColumns<AuditLog> = useMemo(
    () => [
      {
        dataIndex: 'applicationName',
        title: t('applicationName'),
      },
      {
        dataIndex: 'userName',
        title: t('user'),
      },
      {
        dataIndex: 'actionType',
        title: t('action'),
      },
      {
        dataIndex: 'eventType',
        title: t('dataType'),
      },
      {
        dataIndex: 'eventData',
        title: t('dataChanges'),
        render: (_, record) =>
          record.eventObject ? (
            <StyledLink onClick={() => setShowDetails(record)}>
              Details
            </StyledLink>
          ) : (
            record.eventData
          ),
      },
      {
        dataIndex: 'ipAddress',
        title: t('ipAddress'),
      },
      {
        dataIndex: 'latitude',
        title: t('latitude'),
      },
      {
        dataIndex: 'longitude',
        title: t('longitude'),
      },
      {
        dataIndex: 'createAt',
        title: t('dateTime'),
        render: (value) => localDateTimeFormatted(value),
      },
    ],
    [t],
  );

  return (
    <>
      <CustomTable
        loading={loading}
        dataSource={dataSource}
        columns={columns}
        rowKey="id"
        headerActions={{
          right: (
            <Icon
              icon={faArrowsRotate}
              size="lg"
              className="hoverable-icon"
              onClick={reload}
            />
          ),
        }}
      />
      {showDetails ? (
        <Modal
          title="Event Details"
          open
          width={
            isMechanicalInspectionResentEvent(showDetails) ? 800 : undefined
          }
          onCancel={() => setShowDetails(undefined)}
          footer={null}
        >
          <div className="d-flex flex-column gap-2">
            <div className="damage-detail-info mb-3">
              {renderEntityDetails(showDetails)}
            </div>
            <div className="data-changes-section">
              <h6 className="event-action-title mb-2">
                {showDetails.actionType === AuditActionType.Create
                  ? 'A new record was added:'
                  : showDetails.actionType === AuditActionType.Update
                    ? 'The record was updated:'
                    : showDetails.actionType === AuditActionType.Delete
                      ? 'The following record was removed:'
                      : showDetails.actionType === AuditActionType.UnDelete
                        ? 'The following record has been rolled back:'
                        : 'Event details:'}
              </h6>
              {renderDataChanges(showDetails)}
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
};
